"use client";

import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useOptionalAuth } from "@/components/providers/auth-provider";
import { ContestTempNav } from "@/components/auth/ContestTempNav";
import { cn } from "@/lib/utils";
import {
  loadEditorSplitFraction,
  loadOutputSplitFraction,
  saveEditorSplitFraction,
  saveOutputSplitFraction,
} from "../lib/playground-split-storage";

type PlaygroundLayoutProps = {
  toolbar: ReactNode;
  /** Optional time-boxed notice row below the toolbar (the engine release notice). */
  notice?: ReactNode;
  /** Optional row below the main toolbar (e.g. shared snippet author + fork). */
  shareBanner?: ReactNode;
  editor: ReactNode;
  output: ReactNode;
  stdin: ReactNode;
  /** Omitted on SEO pages when the attribution row is a draggable splitter in the parent shell. */
  footer?: ReactNode;
  /**
   * `LN-UI05`: output under the editor at every width. The Learn reader shows
   * the editor in a side pane of about 600 px on a wide screen, where the
   * viewport's `lg` breakpoint would put the output beside the code and cut its
   * lines. Unset, the layout is exactly what it was.
   */
  stacked?: boolean;
  /**
   * `RV-14`: the editor's share of a stacked layout's height, when the caller
   * decides it (the Learn pane gives the code most of the height until there
   * is output). A change sets it again; a drag holds until the next change.
   * Unset, the stacked share is `STACKED_EDITOR_SHARE` as before.
   */
  stackedEditorShare?: number;
  /**
   * `RV-14`: nothing has run yet, so an open stdin keeps only the room the
   * empty output panel and stdin itself need (280 px) and leaves the rest to
   * the code, instead of `PG-UX11`'s 400 px for an output worth reading.
   */
  stackedOutputEmpty?: boolean;
};

/** Always-visible product wordmark — docs/24_PLAYGROUND_SPEC.md §10.7 */
export function PlaygroundIdeWordmark({ newTab = false }: { newTab?: boolean } = {}) {
  const user = useOptionalAuth()?.user ?? null;
  const t = useTranslations("playground");
  const locale = useLocale();
  return (
    <Link
      href={user?.accountKind === "contest_temp" && user.tempContest ? `/${locale}/contests/${user.tempContest.slug}` : `/${locale}`}
      // Inside an embed the wordmark must leave the frame rather than navigate
      // it: a publisher’s article should not turn into progsity.io in place
      // (`PG-EMBED01`).
      {...(newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className="flex min-w-0 shrink-0 items-center gap-1.5 rounded-md sm:gap-2 transition hover:opacity-90 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C]"
      aria-label={t("wordmarkHome")}
    >
      <Image
        src="/icon_logo/logo-white.png"
        alt=""
        width={132}
        height={32}
        className="h-[20px] w-auto sm:h-[26px]"
        priority
        sizes="(max-width: 479px) 96px, (max-width: 640px) 120px, 140px"
      />
      <span className="font-[family-name:var(--font-ubuntu)] text-sm font-semibold leading-none tracking-tight sm:text-base">
        <span className="text-[#E94D1D]">IDE</span>
      </span>
    </Link>
  );
}

type DragKind = "main" | "right" | null;

/**
 * The editor's share of a stacked layout (`PG-UX09`). A stacked layout is the
 * Learn reader's pane, where the share is a height; the main Playground stores
 * its own share as a width (`playground-split-storage.ts`), so a stacked
 * layout neither reads nor writes it: a wide editor on `/playground` would
 * otherwise leave the lesson pane's output a few lines tall.
 */
const STACKED_EDITOR_SHARE = 0.5;

/**
 * Split shell: draggable editor/output width + output/stdin height (docs/24 §10.1).
 */
export function PlaygroundLayout({
  toolbar,
  notice,
  shareBanner,
  editor,
  output,
  stdin,
  footer,
  stacked = false,
  stackedEditorShare,
  stackedOutputEmpty = false,
}: PlaygroundLayoutProps) {
  const user = useOptionalAuth()?.user ?? null;
  const t = useTranslations("playground");

  const [editorShare, setEditorShare] = useState(
    stacked ? (stackedEditorShare ?? STACKED_EDITOR_SHARE) : 0.55,
  );
  const [outputShare, setOutputShare] = useState(0.58);

  const mainRowRef = useRef<HTMLDivElement>(null);
  const rightColRef = useRef<HTMLDivElement>(null);
  const dragKindRef = useRef<DragKind>(null);
  const startRef = useRef({
    x: 0,
    y: 0,
    editorShare: 0.55,
    outputShare: 0.58,
  });
  const editorShareRef = useRef(0.55);
  const outputShareRef = useRef(0.58);
  const stackedRef = useRef(stacked);
  stackedRef.current = stacked;

  useEffect(() => {
    if (stackedRef.current) return;
    setEditorShare(loadEditorSplitFraction());
    setOutputShare(loadOutputSplitFraction());
  }, []);

  useEffect(() => {
    if (!stacked || stackedEditorShare === undefined) return;
    setEditorShare(stackedEditorShare);
  }, [stacked, stackedEditorShare]);

  const clampEditor = (v: number) => Math.min(0.78, Math.max(0.32, v));
  const clampOutput = (v: number) => Math.min(0.88, Math.max(0.22, v));

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const kind = dragKindRef.current;
      if (!kind) return;

      if (kind === "main" && mainRowRef.current) {
        const rect = mainRowRef.current.getBoundingClientRect();
        const lg = !stackedRef.current && window.matchMedia("(min-width: 1024px)").matches;
        const delta = lg
          ? (e.clientX - startRef.current.x) / rect.width
          : (e.clientY - startRef.current.y) / rect.height;
        const next = clampEditor(startRef.current.editorShare + delta);
        editorShareRef.current = next;
        setEditorShare(next);
      } else if (kind === "right" && rightColRef.current) {
        const rect = rightColRef.current.getBoundingClientRect();
        const delta = (e.clientY - startRef.current.y) / rect.height;
        const next = clampOutput(startRef.current.outputShare + delta);
        outputShareRef.current = next;
        setOutputShare(next);
      }
    };

    const onUp = () => {
      if (!dragKindRef.current) return;
      dragKindRef.current = null;
      if (stackedRef.current) return;
      saveEditorSplitFraction(editorShareRef.current);
      saveOutputSplitFraction(outputShareRef.current);
    };

    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
    return () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
    };
  }, []);

  editorShareRef.current = editorShare;
  outputShareRef.current = outputShare;

  const onMainSplitPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragKindRef.current = "main";
    startRef.current = {
      x: e.clientX,
      y: e.clientY,
      editorShare: editorShareRef.current,
      outputShare: outputShareRef.current,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onRightSplitPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragKindRef.current = "right";
    startRef.current = {
      x: e.clientX,
      y: e.clientY,
      editorShare: editorShareRef.current,
      outputShare: outputShareRef.current,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const SPLIT_KEY_STEP = 0.03;

  const onMainSplitKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    let delta = 0;
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") delta = -SPLIT_KEY_STEP;
    else if (e.key === "ArrowRight" || e.key === "ArrowDown") delta = SPLIT_KEY_STEP;
    else return;
    e.preventDefault();
    const next = clampEditor(editorShareRef.current + delta);
    editorShareRef.current = next;
    setEditorShare(next);
    if (!stackedRef.current) saveEditorSplitFraction(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRightSplitKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    let delta = 0;
    if (e.key === "ArrowUp") delta = -SPLIT_KEY_STEP;
    else if (e.key === "ArrowDown") delta = SPLIT_KEY_STEP;
    else return;
    e.preventDefault();
    const next = clampOutput(outputShareRef.current + delta);
    outputShareRef.current = next;
    setOutputShare(next);
    saveOutputSplitFraction(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      {/*
        Opaque, not translucent: `backdrop-filter` is forbidden by `AGENTS.md`
        rule 9 (it degrades on low-end devices), and on a dark shell an opaque
        ground looks the same. Removing it also stops this header creating a
        containing block, which is what used to push `fixed` overlays around
        (`PlaygroundUpgradeModal`).
      */}
      <header className="flex shrink-0 flex-col border-b border-white/10 bg-[#0B0B0C]">
        {user?.accountKind === "contest_temp" ? <ContestTempNav /> : null}
        <div className="flex min-w-0 flex-col gap-2 px-3 py-2.5 sm:gap-2.5 sm:px-4 sm:py-3">
          <div className="flex min-w-0 w-full flex-1 flex-col gap-2 lg:min-h-[44px] lg:justify-center">
            {toolbar}
          </div>
        </div>
        {notice ? (
          <div className="border-t border-white/10 px-3 py-2.5 sm:px-4">{notice}</div>
        ) : null}
        {shareBanner ? (
          <div className="border-t border-white/10 px-3 py-2.5 sm:px-4">{shareBanner}</div>
        ) : null}
      </header>

      <div
        ref={mainRowRef}
        className={cn("flex min-h-0 min-w-0 flex-1 flex-col", !stacked && "lg:flex-row")}
      >
        <section
          className={cn(
            "flex min-h-0 min-w-0 flex-col overflow-hidden border-b border-white/10",
            stacked
              ? "min-h-[160px]"
              : "min-h-[min(32vh,280px)] lg:min-h-0 lg:border-b-0 lg:border-r",
          )}
          style={{
            // Stacked, the editor is the one that gives way when stdin opens
            // (`PG-UX11`, the `has-` rule on the section below).
            flex: `0 ${stacked ? 1 : 0} ${editorShare * 100}%`,
            minWidth: 0,
          }}
        >
          {editor}
        </section>

        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label={t("splitEditorOutputLabel")}
          aria-valuemin={32}
          aria-valuemax={78}
          aria-valuenow={Math.round(editorShare * 100)}
          tabIndex={0}
          onPointerDown={onMainSplitPointerDown}
          onKeyDown={onMainSplitKeyDown}
          className={cn(
            "group relative flex h-2 shrink-0 cursor-row-resize touch-none flex-col items-center justify-center border-y border-white/10 bg-[#0B0B0C] py-0.5 select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45",
            !stacked && "lg:h-auto lg:w-2 lg:cursor-col-resize lg:border-x lg:border-y-0 lg:py-0",
          )}
        >
          <div
            className={cn(
              "h-0.5 w-10 rounded-full bg-white/25 transition group-hover:bg-white/40 group-active:bg-[#E94D1D]/70",
              !stacked && "lg:h-14 lg:w-1 lg:rounded-full",
            )}
            aria-hidden
          />
        </div>

        <section
          className={cn(
            "flex min-h-0 min-w-0 flex-1 flex-col border-t border-white/10",
            // `PG-UX11`: an open stdin (its textarea only exists then) keeps
            // about 270 px for the output, about 115 px of text under a
            // truncation notice, and takes the rest from the editor, which
            // shrinks to its 160 px floor at most. Collapsed, nothing changes.
            stacked
              ? stackedOutputEmpty
                ? "has-[#playground-stdin]:min-h-[280px]"
                : "has-[#playground-stdin]:min-h-[400px]"
              : "lg:min-h-0 lg:border-t-0",
          )}
        >
          <div
            ref={rightColRef}
            className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden"
          >
            {/*
              Stacked (`PG-UX09`): stdin takes its own height and the output
              the rest, so a collapsed stdin is one row rather than 42% of the
              column; the fixed split left the lesson pane's output text 24 px
              tall after a long run. With stdin sizing itself there is nothing
              to drag, so its splitter is not rendered.
            */}
            <div
              className="flex min-h-0 min-w-0 flex-col overflow-hidden"
              style={
                stacked
                  ? { flex: "1 1 0%", minHeight: 120 }
                  : { flex: `0 0 ${outputShare * 100}%`, minHeight: 120 }
              }
            >
              <div className="flex min-h-0 min-w-0 flex-1 flex-col p-2">{output}</div>
            </div>

            {stacked ? null : (
              <div
                role="separator"
                aria-orientation="horizontal"
                aria-label={t("splitOutputStdinLabel")}
                aria-valuemin={22}
                aria-valuemax={88}
                aria-valuenow={Math.round(outputShare * 100)}
                tabIndex={0}
                onPointerDown={onRightSplitPointerDown}
                onKeyDown={onRightSplitKeyDown}
                className="group relative flex h-1.5 shrink-0 cursor-row-resize touch-none flex-col items-center justify-center border-y border-white/10 bg-[#0B0B0C] py-px select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45"
              >
                <div
                  className="h-0.5 w-10 rounded-full bg-white/25 transition group-hover:bg-white/40 group-active:bg-[#E94D1D]/70"
                  aria-hidden
                />
              </div>
            )}

            <div
              className={cn(
                "flex min-h-0 min-w-0 flex-col overflow-hidden",
                stacked ? "border-t border-white/10" : "flex-1",
              )}
              style={
                stacked
                  ? { flex: "0 0 auto" }
                  : { flex: `0 0 ${(1 - outputShare) * 100}%`, minHeight: 96 }
              }
              data-stdin-pane
            >
              {stdin}
            </div>
          </div>
        </section>
      </div>

      {footer != null ? (
        <div className="flex shrink-0 flex-col border-t border-white/10 bg-[#0B0B0C]">{footer}</div>
      ) : null}
    </div>
  );
}
